import React, { useMemo, useState } from "react";
import { countByCompetition } from "../../lib/aggregateAnalytics";
import { COMPETITION_META } from "../../lib/registrationsConfig";
import {
  buildAliasLookup,
  applySchoolAliases,
  filterHiddenRecords,
} from "../../lib/schoolAliases";
import {
  loadAnalyticsConfig,
  saveSchoolAliasGroups,
  saveHiddenSchools,
  saveSchoolOutreach,
  renameSchoolRecords,
} from "../../lib/schoolAliasesFirestore";
import SchoolLeaderboard from "./SchoolLeaderboard";
import RegistrationTimeline from "./RegistrationTimeline";
import CompetitionAnalytics from "./CompetitionAnalytics";
import AnalyticsControlPanel from "./AnalyticsControlPanel";
import {
  loadOutreachTracking,
  updateQuizConfirmationForSchool,
} from "../../lib/outreachTrackingFirestore";
import {
  summarizeQuizBySchool,
  isQuizConfirmed,
} from "../../lib/quizSeason";
import { archiveStaleQuizForSchool } from "../../lib/quizArchiveFirestore";

export default function AnalyticsDashboard({
  records,
  missingTimestamp,
  loading,
  error,
  onRefresh,
  onAnalyticsConfigChange,
}) {
  const [aliasGroups, setAliasGroups] = useState([]);
  const [hiddenSchools, setHiddenSchools] = useState([]);
  const [schoolContacts, setSchoolContacts] = useState([]);
  const [manualSchools, setManualSchools] = useState([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [showControl, setShowControl] = useState(false);
  const [savingAliases, setSavingAliases] = useState(false);
  const [savingHidden, setSavingHidden] = useState(false);
  const [savingOutreach, setSavingOutreach] = useState(false);
  const [renameStatus, setRenameStatus] = useState(null);
  const [schoolState, setSchoolState] = useState({});

  React.useEffect(() => {
    (async () => {
      const tracking = await loadOutreachTracking();
      setSchoolState(tracking.schoolState);

      if (!configLoaded || records.length === 0) return;

      const lookup = buildAliasLookup(aliasGroups);
      const visible = filterHiddenRecords(
        applySchoolAliases(records, lookup),
        hiddenSchools
      );
      const { stale } = summarizeQuizBySchool(visible);
      let reconciled = false;

      for (const key of Object.keys(tracking.schoolState)) {
        if (!isQuizConfirmed(tracking.schoolState[key])) continue;
        if ((stale[key] ?? 0) === 0) continue;
        await archiveStaleQuizForSchool(key, aliasGroups);
        reconciled = true;
      }

      if (reconciled) onRefresh?.();
    })();
  }, [configLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    loadAnalyticsConfig().then(
      ({ groups, hiddenSchools: hidden, schoolContacts: contacts, manualSchools: manual }) => {
      setAliasGroups(groups);
      setHiddenSchools(hidden);
      setSchoolContacts(contacts);
      setManualSchools(manual);
      setConfigLoaded(true);
      onAnalyticsConfigChange?.({ groups, hiddenSchools: hidden });
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = useMemo(
    () => buildAliasLookup(aliasGroups),
    [aliasGroups]
  );

  const mergedRecords = useMemo(
    () => applySchoolAliases(records, lookup),
    [records, lookup]
  );

  const visibleRecords = useMemo(
    () => filterHiddenRecords(mergedRecords, hiddenSchools),
    [mergedRecords, hiddenSchools]
  );

  const hiddenCount = mergedRecords.length - visibleRecords.length;

  const byCompetition = useMemo(
    () => countByCompetition(visibleRecords),
    [visibleRecords]
  );

  const notifyConfigChange = (groups, hidden) => {
    onAnalyticsConfigChange?.({ groups, hiddenSchools: hidden });
  };

  const handleSaveGroups = async (groups) => {
    setSavingAliases(true);
    setRenameStatus(null);
    try {
      await saveSchoolAliasGroups(groups);
      setAliasGroups(groups);
      notifyConfigChange(groups, hiddenSchools);
      setRenameStatus("renaming");
      try {
        await renameSchoolRecords(groups);
        setRenameStatus("done");
        setTimeout(() => setRenameStatus(null), 3000);
      } catch (renameErr) {
        console.error("Record rename failed:", renameErr);
        setRenameStatus("error");
        setTimeout(() => setRenameStatus(null), 5000);
      }
    } catch (err) {
      console.error(err);
      alert("Failed to save school merges. Check console.");
    } finally {
      setSavingAliases(false);
    }
  };

  const handleSaveHiddenSchools = async (nextHidden) => {
    setSavingHidden(true);
    try {
      await saveHiddenSchools(nextHidden);
      setHiddenSchools(nextHidden);
      notifyConfigChange(aliasGroups, nextHidden);
    } catch (err) {
      console.error(err);
      alert("Failed to save hidden schools. Check console.");
    } finally {
      setSavingHidden(false);
    }
  };

  const handleSaveOutreach = async ({ schoolContacts: contacts, manualSchools: manual }) => {
    setSavingOutreach(true);
    try {
      await saveSchoolOutreach({
        schoolContacts: contacts,
        manualSchools: manual,
      });
      setSchoolContacts(contacts);
      setManualSchools(manual);
    } catch (err) {
      console.error(err);
      alert("Failed to save school contacts. Check console.");
    } finally {
      setSavingOutreach(false);
    }
  };

  if (loading || !configLoaded) {
    return (
      <div className="analytics-loading">
        <div className="analytics-spinner" />
        <p>Loading analytics…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-error">
        <p>Failed to load analytics: {error}</p>
        <button className="refresh-btn" onClick={onRefresh}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <button
        type="button"
        className="analytics-control-fab"
        onClick={() => setShowControl(true)}
        title="Open analytics control"
      >
        Control
      </button>

      {showControl && (
        <AnalyticsControlPanel
          records={mergedRecords}
          aliasGroups={aliasGroups}
          hiddenSchools={hiddenSchools}
          onSaveGroups={handleSaveGroups}
          onSaveHiddenSchools={handleSaveHiddenSchools}
          onClose={() => setShowControl(false)}
          saving={savingAliases || savingHidden}
        />
      )}

      {renameStatus === "renaming" && (
        <div className="rename-status rename-status--progress">
          <span className="analytics-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
          Renaming records in Firestore…
        </div>
      )}
      {renameStatus === "done" && (
        <div className="rename-status rename-status--done">
          ✓ Records renamed to canonical school names.
        </div>
      )}
      {renameStatus === "error" && (
        <div className="rename-status rename-status--error">
          ⚠ Aliases saved but some records could not be renamed. Check console.
        </div>
      )}

      {hiddenSchools.length > 0 && (
        <div className="rename-status rename-status--hidden">
          {hiddenSchools.length} school{hiddenSchools.length !== 1 ? "s" : ""}{" "}
          hidden from analytics ({hiddenCount} registration
          {hiddenCount !== 1 ? "s" : ""} excluded) — open Control to manage
        </div>
      )}

      <div className="analytics-topbar">
        <div className="analytics-kpi-headline">
          <span className="headline-number">{visibleRecords.length}</span>
          <span className="headline-label">Total Registrations</span>
          {aliasGroups.length > 0 && (
            <span className="headline-sub">
              {aliasGroups.length} school merge
              {aliasGroups.length !== 1 ? "s" : ""} active
            </span>
          )}
          {hiddenSchools.length > 0 && (
            <span className="headline-sub">
              {hiddenSchools.map((s) => s.label).join(", ")} hidden
            </span>
          )}
        </div>
        <button className="refresh-btn" onClick={onRefresh}>
          Refresh Analytics
        </button>
      </div>

      <div className="comp-count-grid">
        {Object.entries(byCompetition)
          .sort(([, a], [, b]) => b - a)
          .map(([title, count]) => {
            const meta = Object.values(COMPETITION_META).find(
              (m) => m.title === title
            );
            return (
              <div
                key={title}
                className={`comp-count-card pillar-${(meta?.competitionSchool ?? "").toLowerCase()}`}
              >
                <span className="comp-count-number">{count}</span>
                <span className="comp-count-title">{title}</span>
                {meta && (
                  <span className="comp-count-pillar">
                    {meta.competitionSchool}
                  </span>
                )}
              </div>
            );
          })}
      </div>

      <RegistrationTimeline
        records={visibleRecords}
        title="All Registrations Over Time"
      />

      <SchoolLeaderboard
        records={visibleRecords}
        schoolContacts={schoolContacts}
        manualSchools={manualSchools}
        schoolState={schoolState}
        onQuizConfirm={async (schoolKey, payload) => {
          const { schoolState: next } = await updateQuizConfirmationForSchool({
            schoolKey,
            userEmail: "admin",
            confirmType: payload.confirmType,
            returningTeams: payload.returningTeams,
            aliasGroups,
          });
          setSchoolState(next);
          await onRefresh?.();
        }}
        onSaveOutreach={handleSaveOutreach}
        savingOutreach={savingOutreach}
      />

      <CompetitionAnalytics records={visibleRecords} />

      {missingTimestamp > 0 && (
        <p className="analytics-footnote">
          * {missingTimestamp} record{missingTimestamp !== 1 ? "s" : ""} had no
          timestamp and were excluded from time charts.
        </p>
      )}
    </div>
  );
}
